import { PriceRange } from '~/types/axis.types';
import { CreateChartOptions } from '~/types/create.types';
import { TsDataPoint } from '~/types/timeseries.types';
import { extent } from '~/utils/extent/extent';

export const baseYAxisValues = (
  data: TsDataPoint[],
  options: CreateChartOptions,
  customDataRange?: PriceRange | null
) => {
  const dataRange = customDataRange ?? calculateDataRange(data, 'value');

  const bottomMargin = options.gutters.x;
  const axisHeight = options.height - bottomMargin;
  const topAxisPadding = 0.2 * axisHeight;
  const bottomAxisPadding = 0.1 * axisHeight;

  return {
    dataRange,
    options,
    axisHeightScaled: axisHeight - topAxisPadding - bottomAxisPadding,
    bottomMargin,
  };
};
// TODO we could improve these typings with generics
export const calculateDataRange = (data: any[], type?: string): PriceRange => {
  const [min, max] = extent(data, type);

  if (!min || !max) {
    return { min: 0, max: 0, length: 0 };
  }

  return { min, max, length: max - min };
};

interface FindAxisRangeProps {
  options: CreateChartOptions;
  dataRange: PriceRange;
  axisHeightScaled: number;
  invCoordinate: number;
}

export const findAxisRange = ({
  options,
  dataRange,
  axisHeightScaled,
  invCoordinate,
}: FindAxisRangeProps) => {
  const range =
    dataRange.min +
    dataRange.length *
      ((invCoordinate - options.gutters.x) / (axisHeightScaled - 1));

  return range;
};

export const invertCoordinate = (height: number, coordinate: number) => {
  return height - 1 - coordinate;
};

interface YScaleProps {
  dataRange: PriceRange;
  options: CreateChartOptions;
  axisHeightScaled: number;
  bottomMargin: number;
}

interface ValueToCoordinateProps extends YScaleProps {
  value: number;
}

export const valueToCoordinate = ({
  dataRange,
  options,
  axisHeightScaled,
  bottomMargin,
  value,
}: ValueToCoordinateProps) => {
  const invertedCoordinate =
    bottomMargin +
    (axisHeightScaled * (value - dataRange.min)) / dataRange.length;
  return (
    invertCoordinate(options.height, invertedCoordinate) - options.gutters.x
  );
};

interface CalculateAxisTicksProps {
  dataRange: PriceRange;
  options: CreateChartOptions;
  axisHeightScaled: number;
  bottomMargin: number;
}

export const calculateYAxisTicks = ({
  dataRange,
  options,
  axisHeightScaled,
}: CalculateAxisTicksProps) => {
  const baseAxisRangeProps = {
    options,
    dataRange,
    axisHeightScaled,
  };
  let yAxisLabels: number[] = [];

  const bottom = findAxisRange({
    ...baseAxisRangeProps,
    invCoordinate: invertCoordinate(axisHeightScaled, axisHeightScaled - 1),
  });
  const top = findAxisRange({
    ...baseAxisRangeProps,
    invCoordinate: invertCoordinate(options.height, 0),
  });

  const high = Math.max(bottom, top);
  const low = Math.min(bottom, top);

  // TODO maybe find a better way of rounding this
  const tickGap = +((high - low) / 12).toFixed(2);
  let remainder = high % tickGap;
  remainder += remainder < 0 ? tickGap : 0;

  for (let logical = high - remainder; logical > low; logical -= tickGap) {
    yAxisLabels.push(logical);
  }

  return yAxisLabels;
};

export const PADDING_Y_LABEL = 20;

export const calculateYAxisWidth = (
  context: CanvasRenderingContext2D,
  data: TsDataPoint[]
) => {
  const { maxLengthValue } = data.reduce(
    (acc, current) => {
      const currentValueLength = current.value.toString().length;

      if (currentValueLength > acc.maxLength) {
        acc.maxLength = currentValueLength;
        acc.maxLengthValue = current.value;
      }

      return acc;
    },
    {
      maxLength: 0,
      maxLengthValue: 0,
    }
  );

  return context.measureText(maxLengthValue.toString()).width + PADDING_Y_LABEL;
};
